import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { fromPairs, toPairsIn } from 'lodash-es';
import { useHistory, useLocation } from 'umi';
export type CustomObject<K extends string | number, T> = { [key in K]: T };
export type ObjectOfStringValue<T = string> = CustomObject<string, T>;

// 此文件单独针对url进行状态管理
export const useSearchParams = <T extends unknown>(defaultInit?: T) => {
  // 相关知识点: https://developer.mozilla.org/zh-CN/docs/Web/API/URLSearchParams，单独针对react路由6源码中相关路由钩子进行改写，应用到本项目,
  // 需要注意，当前hooks调用会间接导致路由变化会带动当前所引用的组件重新render，如果不确定，可以使用ahooks中useWhyDidYouUpdate来进行排查源头
  const defaultSearchParamsRef = React.useRef(createSearchParams(defaultInit));
  // 获取 location
  const location = useLocation();
  // 解析, 通过对比 更新，这里返回一个非基，用useMemo包裹,减少渲染
  const searchParams = React.useMemo(() => {
    const searchParams = createSearchParams(location.search);
    for (const key of defaultSearchParamsRef.current.keys()) {
      // 表示一个指定的键名对应的值是否存在。
      if (!searchParams.has(key)) {
        // 返回一个tuple,并遍历
        defaultSearchParamsRef.current.getAll(key).forEach((value) => {
          // 插入新搜索参数
          searchParams.append(key, value);
        });
      }
    }
    return searchParams;
  }, [location.search]);

  const navigate = useHistory();
  // 通过 navigate 方法 实现 location.search 的变更,这里返回一个方法，大部分都是在副作用下执行的,用useCallback,把函数引用缓存起来，值变化在渲染
  const setSearchParams = React.useCallback(
    (nextInit: any, navigateOptions?: { replace?: boolean; state?: any; isReplace?: boolean; }) => {
      // fix：修复datalab中，新手引导点击我知道后，在路由回退中又回到了新手引导状态
      // 使用replace替换路由，避免在路由回退时，又回到了新手引导状态
      if (navigateOptions?.isReplace) {
        return navigate.replace(`?${createSearchParams(nextInit)}`, navigateOptions);
      }
      // URLSearchParams toString 就成了 query 格式
      navigate.push(`?${createSearchParams(nextInit)}`, navigateOptions);
    },
    [navigate],
  );

  return [searchParams, setSearchParams] as const;
};

export const createSearchParams = (init: any = ''): URLSearchParams =>
  // 通过原生 api 创建, 数组的话 就类似于 tuple
  new URLSearchParams(
    typeof init === 'string' ||
      Array.isArray(init) ||
      init instanceof URLSearchParams
      ? init
      : Object.keys(init).reduce((memo, key) => {
        const value = init[key];
        return memo.concat(
          Array.isArray(value) ? value.map((v) => [key, v]) : [[key, value]],
        );
      }, [] as any),
  );

// 设置url状态健值对
export const useUrlQueryParam = <K extends string>(keys: K[]) => {
  const [searchParams] = useSearchParams();
  const setSearchParams = useSetUrlSearchParam();
  const [mergeUrlParams, setMergeUrlParams] = useSafeSetState(keys);
  return [
    useMemo(() => {
      const config = {
        ...(subset(fromPairs(searchParams as any), mergeUrlParams) as {
          [key in K]: string;
        }),
        ...mergeUrlParams,
      };
      return config;
    }, [searchParams, mergeUrlParams]),
    (params: Partial<{ [key in K]: unknown }>) => {
      setMergeUrlParams(params as K[]);
      return setSearchParams(params);
    },
  ] as const;
};

// 直接重置url状态的健值对
export const useSetUrlSearchParam = () => {
  const [searchParams, setSearchParam] = useSearchParams();
  const otherPathName = useGetURLParameters();
  return (params: { [key in string]: unknown }) => {
    const o = cleanObject({
      ...otherPathName,
      ...fromPairs(searchParams as any),
      ...params,
    }) as URLSearchParams;
    return setSearchParam(o);
  };
};

// 普获单个url的key值
export const useSearchParamsState = (
  searchParamName: string,
  defaultValue: string,
): readonly [
  searchParamsState: string,
  setSearchParamsState: (newState: string, navigateOptions?: { replace?: boolean; state?: any; isReplace?: boolean; }) => void,
  ] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const acquiredSearchParam = searchParams.get(searchParamName);
  const searchParamsState = acquiredSearchParam ?? defaultValue;
  const setSearchParamsState = (newState: string, navigateOptions?: { replace?: boolean; state?: any; isReplace?: boolean; }) => {
    const next = Object.assign(
      {},
      [...searchParams.entries()].reduce(
        (o, [key, value]) => ({ ...o, [key]: value }),
        {},
      ),
      { [searchParamName]: newState },
    );
    setSearchParams(next, navigateOptions);
  };
  return [searchParamsState, setSearchParamsState];
};

// 普获tuple类型指定的key[]值
export const useSearchParamsAppoint = (): { [key in string]: unknown } => {
  const [searchParams] = useSearchParams();
  return useMemo(
    () =>
      Object.assign(
        {},
        [...searchParams.entries()].reduce(
          (o, [key, value]) => ({ ...o, [key]: value }),
          {},
        ),
        {},
      ),
    [searchParams],
  );
};

// 在组件未卸载后去执行异步回调内的setState
export const useSafeSetState = <T>(
  initialState?: T | (() => T),
): [T | any, React.Dispatch<React.SetStateAction<T>>] => {
  const [state, setState] = useState(initialState);
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const safeSetState = useCallback(
    (args: any) => {
      if (mountedRef.current) {
        return setState(args);
      }
    },
    [mountedRef, setState],
  );

  return [state, safeSetState];
};

// url中search序列化
const useGetURLParameters = () =>
  useMemo(
    () =>
      (window.location.href as any)
        .match(/([^?=&]+)(=([^&]*))/g)
        ?.reduce(
          (a: ObjectOfStringValue, v: string) => (
            (a[v.slice(0, v.indexOf('='))] = v.slice(v.indexOf('=') + 1)), a
          ),
          {},
        ),
    [],
  );

// 查找出URL中出现的key值，
export const subset = <
  O extends { [key in string]: unknown },
  K extends keyof O,
>(
  obj: O,
  keys: K[],
) => {
  const filteredEntries = toPairsIn(obj).filter(([key]) =>
    keys.includes(key as K),
  );
  return fromPairs(filteredEntries) as Pick<O, K>;
};

// 清空对象无效值(null,undefined,'')
export const cleanObject = (object?: Record<string, unknown> | any) => {
  if (!object) {
    return {};
  }
  const result = { ...object };
  Object.keys(result).forEach((key) => {
    const value = result[key];
    if (isVoid(value)) {
      delete result[key];
    }
  });
  return result;
};

// 判断一个类型是否无效
export const isVoid = (value: unknown): boolean =>
  value === void 0 || value === null || value === '';


/**
 * 不区分大小写地从参数对象中提取指定键的值
 * @param params 参数对象
 * @param keys 要提取的键名数组
 * @returns 包含提取值的对象
 */
export function useCaseInsensitiveParams<T extends Record<string, any>>(
  params: T,
  keys: string[]
): Partial<T> {
  return useMemo(() => {
    const result: Partial<T> = {};
    
    // 遍历所有传入的键
    for (const key of keys) {
      // 在参数对象中查找不区分大小写的匹配项
      const matchedKey = Object.keys(params).find(
        paramKey => paramKey.toLowerCase() === key.toLowerCase()
      );
      
      // 如果找到匹配的键，将值添加到结果中
      if (matchedKey) {
        result[key as keyof T] = params[matchedKey];
      }
    }
    
    return result;
  }, [params, keys]);
}